import {
	ArrowUpDown,
	BarChart2,
	Bell,
	Calendar,
	ClipboardList,
	FileText,
	BarChart3,
	Hash,
	Heart,
	Heading,
	Image,
	Layout,
	Link,
	MapPin,
	Maximize,
	Megaphone,
	Menu,
	Newspaper,
	Shield,
	Tag,
	Palette,
	Rss,
	Users,
	UserCircle,
	type LucideIcon
} from "lucide-react";
import { Permissions, type Permission } from "src/constants/permissions";

export interface NavHomeConfig {
	/**
	 * Override the card title on the home page. Use this when the nav
	 * label (which gets context from its parent group) would read
	 * ambiguously on the home page, where each tile stands alone —
	 * e.g. nav "Homepage" → home tile "App homepage". Defaults to the
	 * nav item's own `label` when omitted.
	 */
	title?: string;
	/** Short 1-line copy shown under the card title on the home page. */
	description: string;
	/**
	 * Label for the "Open X →" link on single-destination cards (leaves).
	 * Defaults to `Open <label lowercased>` when omitted.
	 */
	openLinkLabel?: string;
	/**
	 * Render sub-links in a two-column grid inside the card body — used
	 * for group nodes with many children (e.g. App homepage).
	 */
	twoColumn?: boolean;
}

export interface NavItem {
	label: string;
	icon: LucideIcon;
	path?: string;
	/**
	 * When true, this nav item only matches its exact path (passed through
	 * to react-router's NavLink `end` prop). Use this for "Overview"-style
	 * landing pages whose path is a prefix of sibling routes, so the parent
	 * doesn't light up when a child is active.
	 */
	end?: boolean;
	children?: NavItem[];
	guard?: {
		requiredPermission?: Permission;
		restrictedToEstateManager?: boolean;
	};
	/**
	 * When set, this nav item also surfaces as a tile on the admin home
	 * page. Leaf nodes (with `path`) render as single-destination cards
	 * that link to that path. Group nodes (with `children`) render as
	 * multi-destination cards whose sub-links are the annotated node's
	 * direct children. Nodes without `home` are nav-only — but their
	 * descendants are still visited in case THEY carry a `home`
	 * annotation, so a nav group can surface its children on the home
	 * page without appearing as a tile itself.
	 */
	home?: NavHomeConfig;
}

export const navigationConfig: NavItem[] = [
	{
		label: "Analytics",
		icon: BarChart2,
		path: "/admin/analytics",
		guard: { requiredPermission: "CanViewAnalytics" },
		home: {
			description:
				"App and website usage, engagement and retention dashboards.",
			openLinkLabel: "Open analytics"
		}
	},
	{
		label: "Announcements",
		icon: Bell,
		path: "/admin/announcements",
		guard: { requiredPermission: "CanManageAnnouncements" },
		home: {
			description: "Push news and updates to the app and website home feed.",
			openLinkLabel: "Open announcements"
		}
	},
	{
		label: "Events",
		icon: Calendar,
		path: "/admin/app/events",
		guard: { requiredPermission: "CanViewEditEvents" },
		home: {
			description: "Create and manage events shown in the app and website.",
			openLinkLabel: "Open events"
		}
	},
	{
		label: "Homepage",
		icon: Layout,
		home: {
			title: "Homepage",
			description:
				"Branding, banners and what greets users when they open Mee.",
			twoColumn: true
		},
		children: [
			{
				label: "Branding",
				icon: Palette,
				path: "/admin/vendor-sponsorship/app-branding/brands?published=true",
				guard: { requiredPermission: "CanViewEditHomepageContent" }
			},
			{
				label: "Carousel",
				icon: Image,
				path: "/admin/vendor-sponsorship/homepage-carousel/banners?published=true",
				guard: { requiredPermission: "CanViewEditHomepageContent" }
			},
			{
				label: "Key Shouts",
				icon: Megaphone,
				path: "/admin/app/homepage/key-shouts",
				guard: { requiredPermission: "CanViewEditHomepageContent" }
			},
			{
				label: "Ordering",
				icon: ArrowUpDown,
				path: "/admin/app/homepage/ordering",
				guard: { requiredPermission: "CanViewEditHomepageContent" }
			},
			{
				label: "Promotions",
				icon: Tag,
				path: "/admin/vendor-sponsorship/promotion/banners?published=true",
				guard: { requiredPermission: "CanViewEditHomepageContent" }
			},
			{
				label: "Quick Links",
				icon: Link,
				path: "/admin/app/homepage/quick-links",
				guard: { requiredPermission: "CanViewEditHomepageContent" }
			},
			{
				label: "Section Titles",
				icon: Heading,
				path: "/admin/app/homepage/section-titles",
				guard: { requiredPermission: "CanManageSettings" }
			},
			{
				label: "Takeovers",
				icon: Maximize,
				path: "/admin/vendor-sponsorship/full-screen-takeover/banners?published=true",
				guard: { requiredPermission: "CanViewEditHomepageContent" }
			}
		]
	},
	{
		label: "Navigation",
		icon: Menu,
		path: "/admin/app/navigation",
		guard: { requiredPermission: "CanViewEditNavigation" },
		home: {
			description: "Configure the main menu for the app and website.",
			openLinkLabel: "Open navigation"
		}
	},
	{
		label: "Pages",
		icon: FileText,
		path: "/admin/app/pages",
		guard: { requiredPermission: "CanManagePages" },
		home: {
			description: "Create and manage standalone content pages.",
			openLinkLabel: "Open pages"
		}
	},
	{
		label: "News",
		icon: Newspaper,
		path: "/admin/app/news",
		guard: { requiredPermission: "CanManageNews" },
		home: {
			description: "Write and schedule news articles for the app and website.",
			openLinkLabel: "Open news"
		}
	},
	{
		label: "Social Feed",
		icon: Rss,
		home: {
			title: "Social feed",
			description: "Posts, hashtags and saved feeds."
		},
		children: [
			{
				label: "Favourites",
				icon: Heart,
				path: "/admin/social-feed/favourite-feeds",
				guard: { requiredPermission: "CanManageHashtags" }
			},
			{
				label: "Hashtags",
				icon: Hash,
				path: "/admin/social-feed/hashtags",
				guard: { requiredPermission: "CanManageHashtags" }
			},
			{
				label: "Overview",
				icon: Rss,
				path: "/admin/social-feed",
				end: true,
				guard: { requiredPermission: "CanViewSocialPosts" }
			}
		]
	},
	{
		label: "Operations",
		icon: ClipboardList,
		home: {
			description: "Build forms and read reports."
		},
		children: [
			{
				label: "Forms",
				icon: FileText,
				path: "/admin/operations/forms",
				guard: { requiredPermission: "CanViewEditObservationForms" }
			},
			{
				label: "Reports",
				icon: BarChart3,
				path: "/admin/operations/reports",
				guard: { requiredPermission: "CanViewObservationReporting" }
			}
		]
	},
	{
		label: "Users",
		icon: Users,
		home: {
			description: "People, places and permissions."
		},
		children: [
			{
				label: "Groups",
				icon: Users,
				path: "/admin/users/groups",
				guard: { requiredPermission: "CanViewEditGroups" }
			},
			{
				label: "Locations",
				icon: MapPin,
				path: "/admin/users/locations",
				guard: { requiredPermission: "CanViewEditLocations" }
			},
			{
				label: "People",
				icon: UserCircle,
				path: "/admin/users/people",
				guard: { requiredPermission: "CanViewEditPeople" }
			},
			{
				label: "Permissions",
				icon: Shield,
				path: "/admin/users/permissions",
				guard: { requiredPermission: "CanViewEditUserRoles" }
			}
		]
	}
];

interface HomeCardBase {
	key: string;
	label: string;
	icon: LucideIcon;
	description: string;
}

/**
 * A home-page tile derived from the nav tree. Modelled as a discriminated
 * union so exactly one of {@link HomeCardOpenLink.openLink} (single-
 * destination leaf) or {@link HomeCardMultiLink.subLinks} (multi-destination
 * group) is present — never both, never neither. TypeScript enforces this
 * at the call site so we can't accidentally construct a tile in an
 * undefined state.
 */
export type HomeCard = HomeCardOpenLink | HomeCardMultiLink;

export interface HomeCardOpenLink extends HomeCardBase {
	/**
	 * Permission that gates the whole card. Single-destination cards have
	 * no sub-links, so gating happens here at the card level.
	 */
	sectionPermission?: Permission;
	openLink: { label: string; path: string };
	subLinks?: never;
	twoColumn?: never;
}

export interface HomeCardMultiLink extends HomeCardBase {
	/**
	 * Multi-destination cards gate at the sub-link level instead, via
	 * {@link HomeCardSubLink.permission}, so `sectionPermission` is
	 * disallowed here.
	 */
	sectionPermission?: never;
	openLink?: never;
	subLinks: HomeCardSubLink[];
	twoColumn?: boolean;
}

export interface HomeCardSubLink {
	label: string;
	path: string;
	icon: LucideIcon;
	permission?: Permission;
}

/**
 * Walk the nav tree and emit one {@link HomeCard} per node carrying a
 * `home` annotation. Group nodes without `home` are transparent — we
 * recurse into them so their children can surface as cards without the
 * group itself showing up.
 *
 * This keeps the sidebar nav and the admin home page in lockstep: add a
 * `home` annotation to a nav item and it shows up on the home page
 * automatically.
 */
/**
 * Permissions held by every AppUser (the consumer base role). A couple of them
 * gate consumer-facing items that also surface in the admin nav (Social Feed
 * "Overview", Operations "Submissions"), so they must NOT count as "admin
 * access" — otherwise the admin entry point would show to every user.
 */
const CONSUMER_PERMISSIONS = new Set<string>([
	Permissions.CanViewSocialPosts,
	Permissions.CanSubmitSocialPosts,
	Permissions.CanSubmitObservations,
	Permissions.CanUploadMedia,
	Permissions.CanViewNews,
	Permissions.CanManageNotifications
]);

/**
 * The set of permissions that grant access to an admin section — derived from
 * the admin nav guards (so it stays in lockstep: a new admin section extends it
 * automatically), minus the consumer base set. Used to decide whether to
 * surface the admin entry point to a user.
 */
export function collectAdminPermissions(items: NavItem[]): Permission[] {
	const out = new Set<Permission>();
	const walk = (nodes: NavItem[]) => {
		for (const node of nodes) {
			const p = node.guard?.requiredPermission;
			if (p && !CONSUMER_PERMISSIONS.has(p)) out.add(p);
			if (node.children) walk(node.children);
		}
	};
	walk(items);
	return [...out];
}

export function collectHomeCards(items: NavItem[]): HomeCard[] {
	const cards: HomeCard[] = [];
	for (const item of items) {
		if (item.home) {
			cards.push(toHomeCard(item));
		} else if (item.children) {
			cards.push(...collectHomeCards(item.children));
		}
	}
	return cards;
}

function toHomeCard(item: NavItem): HomeCard {
	if (!item.home) {
		// Unreachable by construction — collectHomeCards only calls this
		// for annotated items. Typed defensively.
		throw new Error(`Nav item ${item.label} has no home annotation`);
	}

	const base = {
		key: item.path ?? item.label,
		label: item.home.title ?? item.label,
		icon: item.icon,
		description: item.home.description
	};

	if (item.path) {
		// Leaf → single-destination card.
		return {
			...base,
			sectionPermission: item.guard?.requiredPermission,
			openLink: {
				label: item.home.openLinkLabel ?? `Open ${item.label.toLowerCase()}`,
				path: item.path
			}
		};
	}

	// Group → multi-destination card. Only child leaves (items with a
	// `path`) contribute sub-links; nested groups are ignored at this
	// level to keep the card flat.
	const subLinks: HomeCardSubLink[] = (item.children ?? [])
		.filter((child): child is NavItem & { path: string } => Boolean(child.path))
		.map((child) => ({
			label: child.label,
			path: child.path,
			icon: child.icon,
			permission: child.guard?.requiredPermission
		}));

	return {
		...base,
		subLinks,
		twoColumn: item.home.twoColumn
	};
}
